import { useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { contractPagePreview, type ContractPageItem, type ContractPdfImage, type ContractPdfSignature, type ContractRole, type Language } from "@talentkontor/domain";
import { sanitizeHtml } from "../../../lib/sicheres-html";
import { t } from "../../../lib/sprache";
import { PAPER_TEXT, type SignatureMark } from "./Papier";

/**
 * Seitenansicht eines Vertrags (PARITAET-R3-8, OE-011 des bisherigen Portals): A4-Seiten genau so umbrochen wie das PDF
 * (dieselbe Satzlogik aus contract-pdf.ts), mit Seitenzahl, Blättern, Zoom und Vollbild. Die Blätter sind weiß wie das PDF,
 * auch im dunklen Modus. Unterschriftsbilder erscheinen als Bild, sonst der Name wie im PDF.
 */

const PNG = /^data:image\/png;base64,[A-Za-z0-9+/]+=*$/;
const ZOOMS = [0.75, 1, 1.25, 1.5, 2] as const;

function when(iso: string | null | undefined, timeZone: string, lang: Language): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat(lang === "en" ? "en-GB" : "de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone, timeZoneName: "short" }).format(d);
}

/** Unterschriften fürs Seitenmodell; Bilder als Platzhalter mit Seitenverhältnis 3:1 (die Ansicht setzt das PNG ein). */
export function previewSignatures(marks: readonly SignatureMark[], lang: Language, timeZone: string, roleLabels?: Record<ContractRole, string>): { signatures: ContractPdfSignature[]; pngs: Map<ContractPdfImage, string> } {
  const text = PAPER_TEXT[lang];
  const labels = roleLabels ?? text.roles;
  const pngs = new Map<ContractPdfImage, string>();
  const many = (r: ContractRole) => marks.filter((m) => m.role === r).length > 1;
  const signatures = marks.map((m): ContractPdfSignature => {
    const png = m.signed && m.signatureData && PNG.test(m.signatureData) ? m.signatureData : null;
    const image = png ? { jpeg: new Uint8Array(0), width: 300, height: 100 } : null;
    if (image && png) pngs.set(image, png);
    return {
      role: m.role, slot: m.slot ?? 1, signed: m.signed, name: m.name ?? null, signingName: m.signingName ?? null,
      label: `${labels[m.role]}${many(m.role) ? ` ${m.slot ?? 1}` : ""}${m.capacity === "legal_guardian" ? ` · ${text.guardian}` : ""}`,
      detail: m.signingPosition ?? null, signedAt: m.signed ? when(m.signedAt, timeZone, lang) : null,
      paper: !!m.paper, paperSignedOn: m.paperSignedOn ?? null,
      image,
    };
  });
  return { signatures, pngs };
}

const rgb = (c: readonly number[]) => `rgb(${c.map((v) => Math.round(v * 255)).join(",")})`;

export interface SeitenProps {
  html: string;
  annexHtml?: string | null;
  marks?: readonly SignatureMark[];
  lang: Language;
  timeZone: string;
  roleLabels?: Record<ContractRole, string>;
  title: string;
  /** Agenturfarbe für Kopfzeile und Linie, wie im PDF */
  accent?: string | null;
  /** Vermerk oben rechts auf jeder Seite, wie im PDF (K-513: „Entwurf – nicht unterschrieben“) */
  vermerk?: string | null;
}

type Vorschau = ReturnType<typeof contractPagePreview> & { pngs: Map<ContractPdfImage, string> };

/** Seiten genau wie im PDF (dieselbe Satzlogik aus contract-pdf.ts), mit den Unterschriftsbildern als PNG. */
function useSeiten({ html, annexHtml, marks, lang, timeZone, roleLabels, title, accent, vermerk }: SeitenProps): Vorschau {
  return useMemo(() => {
    const { signatures, pngs } = previewSignatures(marks ?? [], lang, timeZone, roleLabels);
    const p = contractPagePreview({
      language: lang, title, header: title, accent: accent ?? null, bodyHtml: sanitizeHtml(html), stamp: vermerk ?? null,
      annexHtml: annexHtml ? sanitizeHtml(annexHtml) : null, signatures,
    });
    return { ...p, pngs };
  }, [html, annexHtml, marks, lang, timeZone, roleLabels, title, accent, vermerk]);
}

/** Inhalt einer Seite (Text, Linien, Bilder) in Prozent der Seite; die Schrift wächst mit der Seitenbreite (cqw). */
function seitenInhalt(preview: Vorschau, items: readonly ContractPageItem[]) {
  const pct = (v: number, of: number) => `${(v / of) * 100}%`;
  const item = (it: ContractPageItem, key: number) => {
    if (it.t === "text") {
      const style: CSSProperties = {
        left: pct(it.x, preview.width), top: pct(preview.height - it.y, preview.height), fontSize: `${(it.size / preview.width) * 100}cqw`,
        fontWeight: it.bold ? 700 : 400, fontStyle: it.italic ? "italic" : "normal", color: rgb(it.color),
      };
      return <span key={key} className="tk-page-item" style={style}>{it.text}</span>;
    }
    if (it.t === "line") {
      // senkrechte Linie (etwa Spaltengrenze einer Tabelle, Schritt 6): Höhe statt Breite, Linie links
      if (Math.abs(it.x2 - it.x1) < 0.01) {
        const style: CSSProperties = {
          left: pct(it.x1, preview.width), top: pct(preview.height - Math.max(it.y1, it.y2), preview.height), height: pct(Math.abs(it.y2 - it.y1), preview.height),
          borderLeft: `max(1px, ${(it.width / preview.width) * 100}cqw) solid ${rgb(it.color)}`,
        };
        return <span key={key} className="tk-page-rule tk-page-rule-v" style={style} aria-hidden="true" />;
      }
      const style: CSSProperties = {
        left: pct(Math.min(it.x1, it.x2), preview.width), top: pct(preview.height - it.y1, preview.height), width: pct(Math.abs(it.x2 - it.x1), preview.width),
        borderTop: `max(1px, ${(it.width / preview.width) * 100}cqw) solid ${rgb(it.color)}`,
      };
      return <span key={key} className="tk-page-rule" style={style} aria-hidden="true" />;
    }
    const img = preview.images[it.index];
    const src = img ? preview.pngs.get(img) : undefined;
    if (!src) return null;
    const style: CSSProperties = { left: pct(it.x, preview.width), top: pct(preview.height - it.y - it.h, preview.height), width: pct(it.w, preview.width), height: pct(it.h, preview.height) };
    return <img key={key} className="tk-page-img" style={style} src={src} alt={t("Unterschrift", "Signature")} />;
  };
  return items.map(item);
}

export function Seitenansicht({ leiste = false, ...props }: SeitenProps & {
  /** Seitenleiste mit verkleinerten, anklickbaren Seiten (K-519); am Handy eine Zeile mit Seitenzahlen */
  leiste?: boolean;
}) {
  const preview = useSeiten(props);
  const [zoom, setZoom] = useState(1);
  const [current, setCurrent] = useState(0);
  const [full, setFull] = useState(false);
  const root = useRef<HTMLDivElement | null>(null);
  const scroller = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const n = preview.pages.length;
  useEffect(() => {
    const onChange = () => setFull(document.fullscreenElement === root.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);
  const go = (i: number) => {
    const k = Math.max(0, Math.min(n - 1, i));
    const el = pageRefs.current[k];
    const box = scroller.current;
    if (el && box) box.scrollTo({ top: el.offsetTop - 8, behavior: "smooth" });
    setCurrent(k);
  };
  const onScroll = () => {
    const box = scroller.current;
    if (!box) return;
    const top = box.scrollTop + box.clientHeight / 3;
    let k = 0;
    pageRefs.current.forEach((el, i) => { if (el && el.offsetTop <= top) k = i; });
    setCurrent(k);
  };
  const zi = ZOOMS.indexOf(zoom as (typeof ZOOMS)[number]);
  return (
    <div className={leiste ? "tk-pages tk-pages-mit-leiste" : "tk-pages"} ref={root} lang={props.lang}>
      <div className="tk-pages-bar" role="toolbar" aria-label={t("Seitenansicht", "Page view")}>
        <button type="button" onClick={() => go(current - 1)} disabled={current <= 0} aria-label={t("Vorige Seite", "Previous page")}>‹</button>
        <output aria-live="polite">{t(`Seite ${current + 1} von ${n}`, `Page ${current + 1} of ${n}`)}</output>
        <button type="button" onClick={() => go(current + 1)} disabled={current >= n - 1} aria-label={t("Nächste Seite", "Next page")}>›</button>
        <button type="button" onClick={() => setZoom(ZOOMS[Math.max(0, zi - 1)]!)} disabled={zi <= 0} aria-label={t("Verkleinern", "Zoom out")}>−</button>
        <output>{Math.round(zoom * 100)} %</output>
        <button type="button" onClick={() => setZoom(ZOOMS[Math.min(ZOOMS.length - 1, zi + 1)]!)} disabled={zi >= ZOOMS.length - 1} aria-label={t("Vergrößern", "Zoom in")}>+</button>
        {typeof document !== "undefined" && document.fullscreenEnabled && (
          <button type="button" onClick={() => void (full ? document.exitFullscreen() : root.current?.requestFullscreen())}>{full ? t("Vollbild beenden", "Exit full screen") : t("Vollbild", "Full screen")}</button>
        )}
      </div>
      {leiste && (
        <nav className="tk-pages-leiste" aria-label={t("Seiten", "Pages")}>
          {preview.pages.map((items, i) => (
            <button key={i} type="button" className="tk-pages-daumen" aria-current={current === i ? "page" : undefined} onClick={() => go(i)}
              aria-label={t(`Zu Seite ${i + 1} von ${n}`, `Go to page ${i + 1} of ${n}`)}>
              <span className="tk-page tk-page-mini" aria-hidden="true">{seitenInhalt(preview, items)}</span>
              <span className="tk-pages-daumen-nr">{i + 1}</span>
            </button>
          ))}
        </nav>
      )}
      <div className="tk-pages-scroll" ref={scroller} onScroll={onScroll} tabIndex={0} aria-label={t("Vertragsseiten", "Contract pages")}>
        <div className="tk-pages-list" style={{ width: `${zoom * 100}%`, maxWidth: `${zoom * 720}px` }}>
          {preview.pages.map((items, i) => (
            <div key={i} className="tk-page-wrap" ref={(el) => { pageRefs.current[i] = el; }}>
              <div className="tk-page" role="group" aria-label={t(`Seite ${i + 1} von ${n}`, `Page ${i + 1} of ${n}`)}>{seitenInhalt(preview, items)}</div>
              <div className="tk-page-no" aria-hidden="true">{i + 1} / {n}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Seiten zum Drucken (K-513): dieselben A4-Seiten wie im PDF, ohne Bedienleiste, je Blatt eine Seite. Für die Druckansicht
 * („in Seiten drucken“) und den Knopf „Drucken“ der Vorschau.
 */
export function SeitenDruck(props: SeitenProps) {
  const preview = useSeiten(props);
  const n = preview.pages.length;
  return (
    <div className="tk-druckseiten" lang={props.lang}>
      {preview.pages.map((items, i) => (
        <div key={i} className="tk-page" role="group" aria-label={t(`Seite ${i + 1} von ${n}`, `Page ${i + 1} of ${n}`)}>{seitenInhalt(preview, items)}</div>
      ))}
    </div>
  );
}
